import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { Menu, X, Globe, ShoppingBag, ChevronDown, Mail } from 'lucide-react';
import { useCart } from '../lib/cart';

// Menu structure — 6 top-level items + Contact CTA
const MENU = [
  {
    k: 'farm',
    to: '/about',
    children: [
      { to: '/about', k: 'about' },
      { to: '/impact', k: 'impact' },
      { to: '/partners', k: 'partners' },
    ],
  },
  {
    k: 'activities_training',
    to: '/activities',
    children: [
      { to: '/activities', k: 'activities' },
      { to: '/formations', k: 'formation' },
    ],
  },
  { k: 'shop', to: '/shop' },
  { k: 'experiences', to: '/experiences' },
  {
    k: 'stories',
    to: '/news',
    children: [
      { to: '/news', k: 'news' },
      { to: '/gallery', k: 'gallery' },
    ],
  },
  {
    k: 'support',
    to: '/support',
    children: [
      { to: '/support', k: 'support_partner' },
      { to: '/sponsorship', k: 'sponsorship' },
    ],
  },
];

export default function Header() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expanded, setExpanded] = useState(null); // mobile accordion
  const [hoverKey, setHoverKey] = useState(null); // desktop dropdown
  const location = useLocation();
  const cart = useCart();
  const hoverTimer = useRef(null);

  useEffect(() => { setOpen(false); setExpanded(null); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleLang = () => {
    const next = i18n.language?.startsWith('en') ? 'fr' : 'en';
    i18n.changeLanguage(next);
  };

  const openDropdown = (k) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setHoverKey(k);
  };
  const closeDropdown = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setHoverKey(null), 120);
  };

  const isChildActive = (item) => item.children?.some((c) => location.pathname === c.to);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open ? 'glass border-b border-border-soft/60' : 'bg-transparent'
      }`}
    >
      <div className="container-x mx-auto max-w-7xl flex items-center justify-between py-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img
            src="/lawatan-logo.png"
            alt="Centre de Permaculture Lawatan"
            className={`h-14 w-auto transition-all duration-300 group-hover:scale-105 ${
              scrolled || open ? '' : 'drop-shadow-[0_4px_24px_rgba(0,0,0,0.45)]'
            }`}
          />
        </Link>

        {/* DESKTOP NAV */}
        <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
          {MENU.map((it) => {
            const hasChildren = it.children?.length > 0;
            const active = location.pathname === it.to || isChildActive(it);
            const baseClass = `flex items-center gap-1 px-2 xl:px-3 py-2 text-[13px] xl:text-sm font-medium transition-colors whitespace-nowrap rounded-full ${
              scrolled
                ? active ? 'text-forest' : 'text-ink-soft hover:text-forest'
                : active ? 'text-bone' : 'text-bone/85 hover:text-bone'
            }`;

            if (!hasChildren) {
              return (
                <NavLink key={it.k} to={it.to} data-testid={`nav-${it.k}`} className={baseClass}>
                  {t(`nav.${it.k}`)}
                </NavLink>
              );
            }
            return (
              <div
                key={it.k}
                className="relative"
                onMouseEnter={() => openDropdown(it.k)}
                onMouseLeave={closeDropdown}
              >
                <button
                  type="button"
                  data-testid={`nav-${it.k}`}
                  className={baseClass}
                  aria-expanded={hoverKey === it.k}
                  onClick={() => setHoverKey(hoverKey === it.k ? null : it.k)}
                >
                  {t(`nav.${it.k}`)}
                  <ChevronDown size={13} className={`transition-transform ${hoverKey === it.k ? 'rotate-180' : ''}`} />
                </button>
                {hoverKey === it.k && (
                  <div
                    className="absolute left-0 top-full pt-3 min-w-[220px]"
                    data-testid={`dropdown-${it.k}`}
                  >
                    <div className="bg-white rounded-2xl border border-border-soft shadow-2xl overflow-hidden">
                      {it.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={`dropdown-${it.k}-${c.k}`}
                          onClick={() => setHoverKey(null)}
                          className={({ isActive }) =>
                            `block px-5 py-3 text-sm transition-colors ${
                              isActive
                                ? 'bg-sand text-forest font-semibold'
                                : 'text-ink-soft hover:bg-sand/60 hover:text-forest'
                            }`
                          }
                        >
                          {t(`nav.${c.k}`)}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* RIGHT ACTIONS */}
        <div className="flex items-center gap-2">
          {cart && cart.count > 0 && (
            <button
              onClick={() => cart.setOpen(true)}
              data-testid="cart-button"
              aria-label="cart"
              className={`relative grid place-items-center w-9 h-9 rounded-full transition-colors ${
                scrolled || open ? 'bg-forest text-bone hover:bg-leaf' : 'bg-bone/95 text-forest hover:bg-leaf hover:text-white'
              }`}
            >
              <ShoppingBag size={16} />
              <span className="absolute -top-1 -right-1 bg-sun text-ink text-[10px] font-bold rounded-full w-4 h-4 grid place-items-center">
                {cart.count}
              </span>
            </button>
          )}
          <button
            onClick={toggleLang}
            data-testid="lang-toggle-button"
            className={`hidden sm:flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest border rounded-full px-2.5 py-1.5 transition-colors ${
              scrolled || open
                ? 'border-border-soft text-ink hover:bg-sand'
                : 'border-bone/40 text-bone hover:bg-bone/10'
            }`}
          >
            <Globe size={13} />
            <span>{i18n.language?.startsWith('en') ? 'EN' : 'FR'}</span>
          </button>
          {/* Contact CTA — visible desktop only */}
          <NavLink
            to="/contact"
            data-testid="nav-contact-cta"
            className={({ isActive }) =>
              `hidden lg:inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-2 transition-all ${
                scrolled || open
                  ? isActive ? 'bg-forest text-bone' : 'bg-forest text-bone hover:bg-leaf'
                  : isActive ? 'bg-sun text-ink' : 'bg-sun text-ink hover:bg-sun/90'
              }`
            }
          >
            <Mail size={14} /> {t('nav.contact')}
          </NavLink>
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className={`lg:hidden ${scrolled || open ? 'text-ink' : 'text-bone'}`}
            aria-label="menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      {open && (
        <div className="lg:hidden glass border-t border-border-soft/60 max-h-[calc(100vh-80px)] overflow-y-auto" data-testid="mobile-menu">
          <div className="container-x max-w-7xl mx-auto py-6 grid gap-2">
            {MENU.map((it) => {
              const hasChildren = it.children?.length > 0;
              if (!hasChildren) {
                return (
                  <NavLink
                    key={it.k}
                    to={it.to}
                    data-testid={`mnav-${it.k}`}
                    className={({ isActive }) =>
                      `block px-2 py-3 text-base font-medium border-b border-border-soft/40 ${isActive ? 'text-forest' : 'text-ink-soft'}`
                    }
                  >
                    {t(`nav.${it.k}`)}
                  </NavLink>
                );
              }
              const isExp = expanded === it.k;
              return (
                <div key={it.k} className="border-b border-border-soft/40">
                  <button
                    type="button"
                    onClick={() => setExpanded(isExp ? null : it.k)}
                    data-testid={`mnav-${it.k}`}
                    className="w-full flex items-center justify-between px-2 py-3 text-base font-medium text-ink-soft"
                  >
                    <span>{t(`nav.${it.k}`)}</span>
                    <ChevronDown size={16} className={`transition-transform ${isExp ? 'rotate-180' : ''}`} />
                  </button>
                  {isExp && (
                    <div className="pl-4 pb-2 grid gap-1" data-testid={`mnav-${it.k}-panel`}>
                      {it.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={`mnav-${it.k}-${c.k}`}
                          className={({ isActive }) =>
                            `block px-2 py-2 text-sm ${isActive ? 'text-forest font-semibold' : 'text-ink-soft'}`
                          }
                        >
                          {t(`nav.${c.k}`)}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <NavLink
              to="/contact"
              data-testid="mnav-contact"
              className={({ isActive }) =>
                `mt-3 inline-flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-base ${isActive ? 'bg-forest text-bone' : 'bg-sun text-ink'}`
              }
            >
              <Mail size={16} /> {t('nav.contact')}
            </NavLink>
            <button
              onClick={toggleLang}
              data-testid="lang-toggle-mobile"
              className="self-start mt-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest border border-border-soft rounded-full px-3 py-1.5"
            >
              <Globe size={14} /> {i18n.language?.startsWith('en') ? 'EN · FR' : 'FR · EN'}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
